import { fetchBreederAvailability } from 'dpl/breeder_search_results/hooks/useBreederAvailability';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { flatten } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';

import { RESULTS_TYPES } from './constants';

// The group size the mobile surfaces report in: the breeder feed, and the
// similar breed "See all" page inside it. The puppy feed keeps its own, since
// it renders on desktop too and groups in eights there.
export const MOBILE_RESULT_GROUP_SIZE = 4;

function flattenDeliveryMethods(deliveryMethods) {
  return flatten(Object.values(deliveryMethods || {})).map(
    method => method.name
  );
}

// "Never asked" and "asked and got nothing back" are different states, and only
// the first has no honest zero. A caller that opted out of the request never
// measured this row, and the similar breed module's rows each have at least one
// qualifying puppy - a zero there would claim a measurement that never happened.
// A snoozed row is measured without asking, because snoozed means zero, and a
// failed request still asked, so both keep the zeroes they have always reported.
function availabilityDisplayed(result, availabilityMeasured) {
  if (result.analytics_availability_info) {
    return result.analytics_availability_info;
  }

  if (!availabilityMeasured && !result.snoozed) {
    return undefined;
  }

  return {
    puppies: 0,
    upcoming_litters: 0,
    reviews: result.testimonials_count
  };
}

export function resultGroupViewProps(
  breedId,
  resultsType,
  result,
  position,
  page,
  // Whether this row's availability was looked at, either through the request
  // or by being snoozed. Only `sendResultGroupViewedEvent` knows; callers that
  // build props themselves serve puppy rows, which have no availability field.
  { availabilityMeasured = true } = {}
) {
  // Similar breed rows are each listed under a breed of their own, which is not
  // the breed the visitor searched. Every other surface serves one breed's
  // results, where the row's breed and the page's breed are the same and no
  // row carries a `breed` of its own to prefer.
  const resultBreedId = result.breed?.id || breedId;

  let resultProps = {};
  if (resultsType === RESULTS_TYPES.PUPPIES) {
    resultProps = {
      dog_id: result.id,
      puppy_name: result.name,
      breeder_profile_id: result.breeder_profile.id,
      breed_id: resultBreedId,
      miles_away: result.distance_in_miles,
      miles_away_ranking: result.ranking_distance_in_miles,
      transportation_options: flattenDeliveryMethods(
        result.breeder_profile.delivery_methods
      )
    };
  } else {
    // For breeder results, use the analytics props from the service
    const analyticsProps = result.distance_analytics_props || {};
    resultProps = {
      breeder_profile_id: result.id,
      breed_id: resultBreedId,
      miles_away: result.distance_in_miles,
      miles_away_ranking: result.ranking_distance_in_miles,
      // The main feed serves a breeder row flat; similar breed rows nest the
      // breeder under the listing that represents them, so the same methods
      // live one level down.
      transportation_options: flattenDeliveryMethods(
        result.delivery_methods || result.breeder_profile?.delivery_methods
      ),
      availability: result.availability_description,
      miles_away_displayed: analyticsProps.miles_away_displayed,
      logistics_display_type: analyticsProps.logistics_display_type,
      availability_displayed: availabilityDisplayed(
        result,
        availabilityMeasured
      ),
      reviews_score: result.percent_recommend
    };
  }

  return {
    ...resultProps,
    position,
    page_number: page
  };
}

// Breeder availability loads async per card, so the group-viewed event runs
// as a self-contained job: it gathers its own data (through the same
// react-query cache the cards render from, so neither side duplicates the
// other's requests), then fires with complete payloads. Snoozed breeders
// always have empty availability, so they skip the fetch like the card does.
export function sendResultGroupViewedEvent(
  queryClient,
  {
    breedId,
    resultsType,
    resultGroup,
    position,
    page,
    view,
    viewFeature,
    viewFeatureLocation = ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
    // The similar breeds module opts out: its rows belong to breeds other than
    // the page's, so this would have to be keyed per row, and it fires on a
    // surface only the treatment arms render - a request the control arm never
    // makes is a difference between the arms that is not the module itself.
    // Those rows report their availability fields empty instead.
    fetchAvailability = true
  }
) {
  const availabilities = resultGroup.map(result =>
    fetchAvailability &&
    resultsType === RESULTS_TYPES.BREEDERS &&
    !result.snoozed
      ? fetchBreederAvailability(queryClient, breedId, result.id)
      : Promise.resolve(null)
  );

  return Promise.all(availabilities).then(groupAvailabilities => {
    sendRudderstackEvent(ANALYTICS_EVENTS.SEARCH_RESULT_GROUP_VIEWED_BY_USER, {
      view,
      view_feature: viewFeature,
      view_feature_location: viewFeatureLocation,
      results: resultGroup.map((result, groupIdx) =>
        resultGroupViewProps(
          breedId,
          resultsType,
          { ...result, ...groupAvailabilities[groupIdx] },
          position + groupIdx,
          page,
          { availabilityMeasured: fetchAvailability }
        )
      )
    });
  });
}
